import { useState } from 'react';

import { APP_CONFIG_PEXP } from '@hcs/app-configs';
import {
  AvmReportsIncludedIcon,
  ClockRewindIcon,
  DateInput,
  Dropdown,
  DropdownOptionType,
  Flex,
  Input,
} from '@hcs/design-system';
import { InputLayout } from '@hcs/forms';
import { usePropertyStatePreview } from '@hcs/property-state';
import { useOrgIsSelfServe } from '@hcs/self-serve-info';
import {
  CerberusInputHcs,
  DateStr,
  ReportConfigSlugs,
  ReportCreateInputs,
  ReportWithData,
} from '@hcs/types';

import { useCreateReport, useReports } from '../../hooks';
import { PropertyPurchasePexpReport } from '../PropertyPurchasePexpReport/PropertyPurchasePexpReport';
import { ReportsList } from '../ReportsList';

import styles from './PropertyPurchasePexpReport.module.css';

const REPORT_CONFIG_SLUG_OPTIONS: DropdownOptionType<
  ReportConfigSlugs.PexpDynamic | ReportConfigSlugs.PexpEffectiveDateDynamic
>[] = [
  {
    label: (
      <Flex>
        <AvmReportsIncludedIcon />
        <b>Today</b> Refresh Data for 30 Days
      </Flex>
    ),
    value: ReportConfigSlugs.PexpDynamic,
  },
  {
    label: (
      <Flex>
        <ClockRewindIcon /> <b>Past Effective Date</b> Data as of a specific
        date
      </Flex>
    ),
    value: ReportConfigSlugs.PexpEffectiveDateDynamic,
  },
];

const CAPABILITY_KEY = 'pexp-dynamic-report';
export const PropertyPurchasePexpDynamic = ({
  cerberusInput,
  clientFileId: clientFileIdProp,
  actionPortalIdRender,
  onCreateSuccess,
  onCancel,
}: {
  cerberusInput: CerberusInputHcs;
  clientFileId?: string;
  actionPortalIdRender?: string;
  onCreateSuccess?: (
    report: ReportWithData,
    variables: ReportCreateInputs,
  ) => void;
  onCancel: VoidFunction;
}) => {
  const [clientFileId, setClientFileId] = useState<string | null>(
    clientFileIdProp || null,
  );
  const { data: isSelfServe } = useOrgIsSelfServe();
  const { mutate: createReport } = useCreateReport(
    {
      onSuccess: onCreateSuccess,
    },
    { closeToastOnSuccess: true },
  );
  const [reportConfigSlug, setReportConfigSlug] = useState<
    ReportConfigSlugs.PexpDynamic | ReportConfigSlugs.PexpEffectiveDateDynamic
  >(ReportConfigSlugs.PexpDynamic);
  const [effectiveDate, setEffectiveDate] = useState<DateStr | null>(null);
  const { data: propertyStatePreview } = usePropertyStatePreview(cerberusInput);
  const reportSearchInputsActive = {
    activeStatus: 'active' as const,
    slug: cerberusInput.slug || undefined,
    reportConfigSlug: [
      ReportConfigSlugs.PexpDynamic,
      ReportConfigSlugs.PexpEffectiveDateDynamic,
    ],
  };
  const activeReportsQuery = useReports(reportSearchInputsActive, {
    enabled: !!reportSearchInputsActive.slug,
  });
  const activePexpReports = activeReportsQuery.data;
  const date = effectiveDate && new Date(effectiveDate);
  const today = new Date();
  const dateIsValid = date && date < today;

  const hcAddressId = propertyStatePreview?.propertyState?.hcAddressId;
  if (!hcAddressId) {
    return null;
  }
  const handleCancel = () => {
    onCancel();
    setEffectiveDate(null);
  };
  return (
    <PropertyPurchasePexpReport
      cerberusInput={cerberusInput}
      capabilityKey={CAPABILITY_KEY}
      reportConfigSlug={reportConfigSlug}
      title={
        <>
          <APP_CONFIG_PEXP.Icon size="lg" />
          Property Explorer - Custom Valuation Report
        </>
      }
      actionPortalIdRender={actionPortalIdRender}
      isAdditionalReport={
        activePexpReports && activePexpReports.data.length > 0
      }
      onCancel={handleCancel}
      onSubmit={() => {
        createReport({
          hcAddressId: Number(hcAddressId),
          reportConfigSlug,
          clientFileId: clientFileId || undefined,
          effectiveDate:
            (reportConfigSlug === ReportConfigSlugs.PexpEffectiveDateDynamic &&
              effectiveDate) ||
            undefined,
        });
      }}
      submitDisabled={
        reportConfigSlug === ReportConfigSlugs.PexpEffectiveDateDynamic
          ? date == null || !dateIsValid
          : false
      }
    >
      {activePexpReports && activePexpReports.data.length > 0 && (
        <div className={styles.ReportsListSection}>
          <h3 className={styles.Heading}>Active Custom Valuation Reports</h3>
          <ReportsList
            highlightOnHover
            reportSearchInputs={reportSearchInputsActive}
            linkToReport
          />
        </div>
      )}
      <InputLayout labelWidth={150} label="Effective Date" required>
        <Dropdown
          dataHcName="pexp-purchase-type-dropdown"
          value={reportConfigSlug}
          theme={{
            Dropdown: styles.DateDropdown,
            SelectContainer: styles.DateDropdownSelectContainer,
            SelectElement: styles.DateDropdownSelectElement,
          }}
          options={REPORT_CONFIG_SLUG_OPTIONS}
          onSelect={(newReportConfigSlug) => {
            setReportConfigSlug(newReportConfigSlug);
            setEffectiveDate(null);
          }}
        />
      </InputLayout>
      {reportConfigSlug === ReportConfigSlugs.PexpEffectiveDateDynamic && (
        <InputLayout labelWidth={150} label={null} required={false}>
          <DateInput
            dataHcName="pexp-effective-date"
            placeholder="Set Report Date"
            value={effectiveDate}
            onChange={(v) => {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              setEffectiveDate(v ? (v as DateStr) : null);
            }}
            error={
              date && !dateIsValid
                ? 'Effective date must be in the past'
                : undefined
            }
          />
        </InputLayout>
      )}
      {
        // Not showing this feature to self-serve users to simplify the UI for them
        !isSelfServe && (
          <InputLayout
            labelWidth={150}
            label="Client File ID"
            labelHelper={{
              dataHcName: 'client-file-id-helper',
              helperContent:
                'Optional Identifier to link reports and addresses to your internal systems',
            }}
            required={false}
          >
            <Input
              noCapture
              dataHcName="client-file-id"
              value={clientFileId || ''}
              onChange={setClientFileId}
            />
          </InputLayout>
        )
      }
    </PropertyPurchasePexpReport>
  );
};
